import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Loader2, ShieldCheck, User, Users, Calendar, BarChart3, Send, Settings as SettingsIcon } from "lucide-react";
import BookingsCalendar from "@/components/calendar/BookingsCalendar";
import VendorBookingsList from "@/components/vendor/VendorBookingsList";
import FleetUtilizationPanel from "@/components/admin/FleetUtilizationPanel";
import { SettingsTab, NotificationsTab } from "@/components/admin/AdminOpsPanel";
import ChangePasswordCard from "@/components/profile/ChangePasswordCard";
import ManualBookingDialog from "@/components/booking/ManualBookingDialog";
import VendorAccessTrigger from "@/components/admin/VendorAccessTrigger";
import { COUNTRY_OPTIONS } from "@/lib/countries";

const PERMISSION_LABELS = {
  view_bookings: "View bookings",
  view_users: "View users / vendors",
  view_payouts: "View payouts & fleet",
  approve_vendors: "Approve vendors",
  refund_payments: "Refund payments",
  cancel_bookings: "Cancel bookings",
  release_deposits: "Release deposits",
  send_notifications: "Send broadcasts",
  edit_platform_settings: "Edit platform settings",
  message_users: "Message users",
  manual_bookings: "Create manual bookings",
};

export default function AgentDashboard() {
  const { user, refresh } = useAuth();
  const perms = useMemo(() => new Set(user?.permissions || []), [user]);
  const has = (p) => perms.has(p);
  const isActive = user?.is_active !== false;

  return (
    <div>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="agent-dashboard">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
          <h1 className="font-display text-3xl lg:text-4xl tracking-tight font-medium">Agent panel</h1>
          <Badge variant={isActive ? "default" : "destructive"} data-testid="agent-status-badge">
            {isActive ? "Active" : "Disabled by admin"}
          </Badge>
        </div>
        <p className="text-neutral-600 text-sm mb-10">Welcome back, {user?.name || user?.email}. Your access below is configured by an administrator.</p>

        {!isActive && (
          <div className="mb-8 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="agent-disabled-banner">
            Your account has been disabled. You can update your profile, but admin tools are read-locked until an administrator re-enables you.
          </div>
        )}

        <ProfileCard onSaved={refresh} />

        <ChangePasswordCard />

        <AccessCard perms={user?.permissions || []} />

        {has("view_users") && (
          <Section title="Vendors" icon={Users} testid="agent-section-vendors">
            <VendorsTable canApprove={has("approve_vendors") && isActive} />
          </Section>
        )}

        {has("view_bookings") && (
          <Section title="Live bookings calendar" icon={Calendar} testid="agent-section-calendar">
            <BookingsCalendar scope="admin" />
          </Section>
        )}

        {has("view_bookings") && (
          <Section title="All bookings" icon={Calendar} testid="agent-section-bookings">
            {has("manual_bookings") && (
              <div className="mb-4 flex items-center justify-between flex-wrap gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3 px-4" data-testid="agent-manual-booking-banner">
                <div className="text-xs text-emerald-800"><strong>Manual bookings enabled.</strong> Record off-platform reservations on any car.</div>
                <ManualBookingDialog />
              </div>
            )}
            <BookingsList />
          </Section>
        )}

        {has("view_payouts") && (
          <Section title="Fleet utilization" icon={BarChart3} testid="agent-section-fleet">
            <FleetUtilizationPanel />
          </Section>
        )}

        {has("send_notifications") && (
          <Section title="Broadcasts" icon={Send} testid="agent-section-notifications">
            <NotificationsTab />
          </Section>
        )}

        <Section title="Platform settings" icon={SettingsIcon} testid="agent-section-settings">
          <SettingsTab canEdit={has("edit_platform_settings") && isActive} />
        </Section>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, icon: Icon, testid, children }) {
  return (
    <section className="mt-12" data-testid={testid}>
      <h2 className="font-display text-2xl font-medium mb-5 flex items-center gap-2.5">
        <Icon className="h-5 w-5 text-neutral-500" />
        {title}
      </h2>
      {children}
    </section>
  );
}

function ProfileCard({ onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", address: "", country: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        address: user.address || "",
        country: user.country || "",
      });
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/profile", {
        name: form.name.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        country: form.country || null,
      });
      toast.success("Profile updated");
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-3xl border border-border bg-white p-6" data-testid="agent-profile-card">
      <h2 className="font-display text-xl font-medium mb-1 flex items-center gap-2"><User className="h-5 w-5 text-neutral-500" />Your profile</h2>
      <p className="text-xs text-neutral-500 mb-5">Update your contact details. Your email and role can only be changed by an administrator.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
        <div>
          <Label>Email</Label>
          <Input value={user?.email || ""} disabled className="mt-1 rounded-xl bg-neutral-50" data-testid="agent-profile-email" />
        </div>
        <div>
          <Label>Full name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" data-testid="agent-profile-name" />
        </div>
        <div>
          <Label>Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-xl" placeholder="+33 6 12 34 56 78" data-testid="agent-profile-phone" />
        </div>
        <div>
          <Label>Country</Label>
          <Select value={form.country || "__none__"} onValueChange={(v) => setForm({ ...form, country: v === "__none__" ? "" : v })}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="agent-profile-country"><SelectValue /></SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map((c) => (
                <SelectItem key={c.code || "__none__"} value={c.code || "__none__"}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Address</Label>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 rounded-xl" placeholder="Street, city, postcode" data-testid="agent-profile-address" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="rounded-full mt-5 bg-blue-600 hover:bg-blue-700" data-testid="agent-profile-save">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save profile
      </Button>
    </section>
  );
}

function AccessCard({ perms }) {
  return (
    <section className="rounded-3xl border border-border bg-white p-6 mt-8" data-testid="agent-access-card">
      <h2 className="font-display text-xl font-medium mb-1 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" />Your access</h2>
      <p className="text-xs text-neutral-500 mb-4">Granted by the administrator. Sections below this card appear automatically based on these permissions.</p>
      {perms.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-6 text-sm text-neutral-500" data-testid="agent-access-empty">
          You don't have any permissions yet. Contact an administrator to request access.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="agent-access-perms">
          {perms.map((p) => (
            <Badge key={p} variant="default" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200" data-testid={`agent-perm-${p}`}>
              {PERMISSION_LABELS[p] || p.replace(/_/g, " ")}
            </Badge>
          ))}
        </div>
      )}
    </section>
  );
}

function VendorsTable({ canApprove }) {
  const [vendors, setVendors] = useState([]);
  const [query, setQuery] = useState("");
  const load = () => api.get("/admin/vendors").then((r) => setVendors(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return vendors;
    return vendors.filter((v) => {
      return (
        (v.name || "").toLowerCase().includes(q) ||
        (v.email || "").toLowerCase().includes(q) ||
        (v.business_name || "").toLowerCase().includes(q)
      );
    });
  }, [vendors, q]);

  const approve = async (id) => {
    try { await api.post(`/admin/vendors/${id}/approve`); toast.success("Vendor approved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/vendors/${id}/reject`); toast.success("Vendor rejected"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search vendors by name, email, or business…"
        className="mb-4 rounded-full h-11 max-w-md"
        data-testid="agent-vendor-search"
      />
      <div className="rounded-2xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left">
            <tr>
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Business</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => (
              <tr key={v.id} className="border-t border-border" data-testid={`agent-vendor-row-${v.id}`}>
                <td className="px-5 py-4">{v.name}</td>
                <td className="px-5 py-4 text-neutral-600">{v.email}</td>
                <td className="px-5 py-4 text-neutral-600">{v.business_name || "—"}</td>
                <td className="px-5 py-4">
                  <Badge
                    variant={v.vendor_status === "approved" ? "default" : v.vendor_status === "rejected" ? "destructive" : "secondary"}
                    className={v.vendor_status === "approved" ? "bg-emerald-500 hover:bg-emerald-600 text-white border-transparent" : ""}
                  >{v.vendor_status}</Badge>
                </td>
                <td className="px-5 py-4 text-right space-x-2 whitespace-nowrap">
                  <VendorAccessTrigger vendor={v} />
                  {canApprove && v.vendor_status !== "approved" && <Button size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700" onClick={() => approve(v.id)} data-testid={`agent-approve-${v.id}`}>Approve</Button>}
                  {canApprove && v.vendor_status !== "rejected" && <Button size="sm" variant="ghost" className="rounded-full" onClick={() => reject(v.id)} data-testid={`agent-reject-${v.id}`}>Reject</Button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-neutral-500">
                {q ? `No vendors match “${query}”.` : "No vendors yet."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BookingsList() {
  const [bookings, setBookings] = useState([]);
  const load = () => api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => {
    const arr = [...bookings];
    arr.sort((a, b) => new Date(b.created_at || b.start_date || 0) - new Date(a.created_at || a.start_date || 0));
    return arr;
  }, [bookings]);

  return (
    <div data-testid="agent-bookings-list">
      <div className="text-xs text-neutral-500 mb-3">Latest first · {sorted.length} total</div>
      <VendorBookingsList
        bookings={sorted}
        depositLoadingId={null}
        onRelease={() => toast.info("Ask an admin or the vendor — deposit actions are vendor-routed.")}
        onWithhold={() => toast.info("Ask an admin or the vendor — deposit actions are vendor-routed.")}
        onChanged={load}
      />
    </div>
  );
}
